import type { RunStatus, StepStatus } from "@shared/types/runs";
import { isTerminalRun } from "@/components/runs/runFormatting";
import { transformSkipReasonWord } from "@shared/runs/transformSkipReasons";

/**
 * #1083: why a run's dbt results list no test or no model, in words that are true for the run as
 * it stands. One rule for the run page's dbt results (`TransformResultsView`), a dbt job's
 * History (`TransformationHistoryView`, its expanded run and its Tests cell) and the Tests panel.
 *
 * In order:
 * - Run tests off: the server's `testsRequested === false`, read from what the transform step
 *   recorded when it started dbt, so it holds for an active run too.
 * - "yet" only while the run can still report (the RUN is queued or running).
 * - Counted, not listed: an older runner counted tests (`testsTotal`) or models
 *   (`modelsReported`) in its summary but wrote no per-node rows. They ran, so this comes first.
 * - Cancelled (#1444): dbt started and the run was stopped, so it cannot say no
 *   model ran. A step cancelled before dbt started (`transformStarted === false`) is Stopped.
 * - Stopped: the TRANSFORM STEP failed or was cancelled (a run of imports and dbt whose loads
 *   landed and whose dbt failed ends `partially_succeeded`, so the run status alone cannot say it).
 *   Without the step, a failed or cancelled run reads as stopped.
 * - Skipped: the transform step was skipped (no repository, a stale source, ...), named by its
 *   skip reason when the row carries one. A skipped step is not a stopped run: the run may succeed.
 * - Otherwise nothing ran.
 */
export interface EmptyResultsFacts {
  /** The run's status; absent reads as a run still active. */
  runStatus: RunStatus | undefined;
  /** The run's transform step status, when the run has one. */
  transformStatus?: StepStatus | undefined;
  /** The transform step's `TRANSFORM_SKIP_REASONS` token, when it was skipped. */
  skippedReason?: string | null | undefined;
  /** The transform step's summary counts (`metrics.testsTotal`, `metrics.modelsReported`). */
  testsTotal?: number | null | undefined;
  modelsReported?: number | null | undefined;
  /**
   * Whether dbt ever started (the transform step's `startedAt` is set). A step cancelled while
   * still pending never launched dbt, so it sent nothing to the warehouse. Absent reads as started.
   */
  transformStarted?: boolean | undefined;
}

// True for a run of dbt jobs and for a run of chosen models (which names no dbt job) alike.
export const TESTS_OFF_SENTENCE = "Run tests was off for this run.";
// #1444: the same for tests, when dbt started before the cancel.
export const TESTS_CANCELLED_SENTENCE =
  "The run was stopped. dbt did not report which tests finished.";
export const TESTS_STOPPED_SENTENCE = "The run stopped before tests ran.";
export const MODELS_STOPPED_SENTENCE = "The run stopped before any model ran.";
// #1444: a cancelled run may have built models before it stopped, so it never claims none ran.
export const MODELS_CANCELLED_SENTENCE =
  "The run was stopped. dbt did not report which models finished, and statements already sent to Snowflake may have completed.";
export const TESTS_NOT_RECORDED_SENTENCE = "Per-test results are not recorded for this run.";
export const MODELS_NOT_RECORDED_SENTENCE = "Per-model results are not recorded for this run.";

type Why = "active" | "stopped" | "cancelled" | "skipped" | "ended";

function whyEmpty({ runStatus, transformStatus, transformStarted }: EmptyResultsFacts): Why {
  if (!runStatus || !isTerminalRun(runStatus)) return "active";
  if (transformStatus === "skipped") return "skipped";
  if (transformStatus) {
    if (transformStatus === "cancelled")
      return transformStarted === false ? "stopped" : "cancelled";
    return transformStatus === "failed" ? "stopped" : "ended";
  }
  if (runStatus === "cancelled") return transformStarted === false ? "stopped" : "cancelled";
  return runStatus === "failed" ? "stopped" : "ended";
}

/** "The dbt step was skipped, so no test ran.", with the reason when the row names one. */
function skippedSentence(reason: string | null | undefined, what: "test" | "model"): string {
  // A token with no phrase reads as no reason: never the raw token.
  const words = transformSkipReasonWord(reason);
  return words
    ? `The dbt step was skipped because ${words}, so no ${what} ran.`
    : `The dbt step was skipped, so no ${what} ran.`;
}

export function testsEmptyMessage(
  testsRequested: boolean | null | undefined,
  facts: EmptyResultsFacts,
): string {
  if (testsRequested === false) return TESTS_OFF_SENTENCE;
  const why = whyEmpty(facts);
  if (why === "active") return "No test has reported for this run yet.";
  // Counted by the summary, so they ran, whatever came after.
  if ((facts.testsTotal ?? 0) > 0) return TESTS_NOT_RECORDED_SENTENCE;
  if (why === "cancelled") return TESTS_CANCELLED_SENTENCE;
  if (why === "stopped") return TESTS_STOPPED_SENTENCE;
  if (why === "skipped") return skippedSentence(facts.skippedReason, "test");
  return "No test ran in this run.";
}

export function modelsEmptyMessage(facts: EmptyResultsFacts): string {
  const why = whyEmpty(facts);
  if (why === "active") return "No model has reported for this run yet.";
  if ((facts.modelsReported ?? 0) > 0) return MODELS_NOT_RECORDED_SENTENCE;
  if (why === "cancelled") return MODELS_CANCELLED_SENTENCE;
  if (why === "stopped") return MODELS_STOPPED_SENTENCE;
  if (why === "skipped") return skippedSentence(facts.skippedReason, "model");
  return "No model ran in this run.";
}

/** The step's facts for the helper from a run step row's metrics (the run page). */
export function transformStepFacts(
  step: { status: StepStatus; metrics: unknown; startedAt?: string | null } | undefined,
): Omit<EmptyResultsFacts, "runStatus"> {
  if (!step) return {};
  const metrics =
    step.metrics && typeof step.metrics === "object" && !Array.isArray(step.metrics)
      ? (step.metrics as Record<string, unknown>)
      : {};
  const number = (key: string) => (typeof metrics[key] === "number" ? metrics[key] : null);
  return {
    transformStatus: step.status,
    ...(step.startedAt !== undefined ? { transformStarted: step.startedAt !== null } : {}),
    skippedReason: typeof metrics.skippedReason === "string" ? metrics.skippedReason : null,
    testsTotal: number("testsTotal"),
    modelsReported: number("modelsReported"),
  };
}
